<template>
  <teleport to="head">
    <MetaTags :meta-data-fn="metaDataFn" />
    <ConfigState />
  </teleport>
  <div class="max-w-full min-h-[100vh] flex flex-col h-screen overflow-hidden">
    <Transition
      enter-active-class="transition-opacity duration-250 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-250 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <Sidebar v-if="userStore.isSidebarVisible" />
    </Transition>
    <div v-if="!disableSSR" class="scroll-container w-full overflow-y-auto">
      <div class="min-h-[100vh] flex flex-col">
        <Header />
        <main class="flex-grow flex flex-col">
          <RouterView />
        </main>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MetaTags } from '@/components/MetaTags'
import { useMeta } from '@/composables/Meta'
import { computed, getCurrentInstance, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { ConfigState } from '@/components/ConfigState'
import Config from '@/utils/config'
import Header from '@/components/Header.vue'
import Sidebar from '@/components/Sidebar.vue'
import UserService from '@/services/UserService'

const { metaDataFn, updateMetaData } = useMeta()
const { t } = useI18n()
const app = getCurrentInstance()
const fullUrl = app?.appContext.config.globalProperties.fullUrl
const userStore = useUserStore()
const route = useRoute()

onMounted(() => {
  if (userStore.isLoggedIn) {
    UserService.loadInfo()
  }
})

onServerPrefetch(() => {
  if (!language.value) return
  userStore.setLanguage(language.value)
})

const disableSSR = computed(() => {
  if (!Config.ssr) return false
  return route.matched.some((record) => {
    return record.meta.ssr != undefined && record.meta.ssr === false
  })
})

const language = computed((): string => {
  return (route.params.locale as string) ?? userStore.language ?? Config.languages?.[0]
})

const resetMetaData = () => {
  return updateMetaData({
    language: computed(() => userStore.language),
    title: computed(() => t('common.seo.title')),
    description: computed(() => t('common.seo.description')),
    image: computed(() => t('common.seo.image')),
    noIndex: computed(() => false),
    url: computed(() => (Config.ssr ? fullUrl : window.location.href)),
    next: computed(() => undefined),
    prev: computed(() => undefined),
  })
}

resetMetaData()

watch(
  () => route.path,
  () => {
    resetMetaData()
  }
)

watch(
  () => language.value,
  (newLanguage) => {
    if (!newLanguage) return
    userStore.setLanguage(newLanguage)
  },
  { immediate: true }
)
</script>
